/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * What the pending band counts.
 *
 * Every entry here must correspond to something the screen already renders
 * as absent — a field in `--accent-cobre`, a portal switched off. The two
 * have to agree in both directions: a value written as "não informado" with
 * no chip is a pendency the user cannot find, and a chip with no
 * correspondingly marked value sends them to a tab where nothing looks wrong.
 */

import type { TFunction } from 'i18next';
import type { AgencyValuesType } from '@pages/authenticated/agency-pages/AgenciesContext';
import { isSettingsSectionVisible, type SettingsNavOptions, type SettingsSection } from '../constants/settings-nav';

/** One clickable chip in the pending band. */
export interface SettingsPendingItem {
  /** Stable key, also the testid suffix. */
  key: string;
  /** The tab the chip navigates to. */
  section: SettingsSection;
  /** Already-localized chip label. */
  label: string;
}

/** The portal facts the band needs; a subset of `PortalSettings`. */
export interface PortalPendingInput {
  connected_portals: string[];
  enabled_portals: string[];
}

export interface BuildPendingInput {
  agency: AgencyValuesType;
  /** Null while portal settings are still loading, or when the fetch failed. */
  portals: PortalPendingInput | null;
  /** Localized portal labels by id, for naming the portal in the chip. */
  portalLabels: Record<string, string>;
  t: TFunction;
  /** The same gating options that filter the tabs. */
  navOptions: SettingsNavOptions;
}

/**
 * The registration fields the profile block renders, in display order.
 *
 * This list *is* the pendency source for the profile: the band counts the
 * same fields the block marks in copper, so the two cannot disagree.
 */
const PROFILE_REQUIRED_FIELDS: Array<{ key: string; labelKey: string; read: (a: AgencyValuesType) => string | undefined | null }> = [
  { key: 'cnpj', labelKey: 'v2.profile.cnpj', read: (a) => a.cnpj || a.cpf },
  { key: 'creci', labelKey: 'v2.profile.creci', read: (a) => a.creci },
  { key: 'phone', labelKey: 'agencyData.labels.telefone', read: (a) => a.phone },
  { key: 'email', labelKey: 'v2.profile.email', read: (a) => a.email },
];

/** The field keys the band counts, and so the only ones drawn in copper. */
export const COUNTED_PROFILE_FIELD_KEYS: ReadonlySet<string> =
  new Set(PROFILE_REQUIRED_FIELDS.map((field) => field.key));

/**
 * Marks as optional every profile field this module does not count.
 *
 * Lives here, beside {@link COUNTED_PROFILE_FIELD_KEYS}, because the colour
 * and the chip must be decided by one list. Flagged by hand in the section
 * they drifted immediately: an agency with no website was drawn in copper,
 * promising a chip the band never raises.
 *
 * @param fields - The fields as the block wants to draw them.
 * @returns The same fields, with `optional` set from the band's source.
 */
export function withCopperOnlyForCountedFields<T extends { key: string }>(fields: T[]): Array<T & { optional: boolean }> {
  return fields.map((field) => ({
    ...field,
    optional: !COUNTED_PROFILE_FIELD_KEYS.has(field.key),
  }));
}

/**
 * The profile fields that carry no value.
 *
 * @param agency - The active agency.
 * @returns The descriptors of the empty fields, in display order.
 */
export function findEmptyProfileFields(agency: AgencyValuesType) {
  return PROFILE_REQUIRED_FIELDS.filter((field) => {
    const value = field.read(agency);
    return !value || value.trim() === '';
  });
}

/**
 * Builds the profile chip, if the profile is missing anything.
 *
 * One missing field names itself ("Perfil: falta CNPJ"); several are counted
 * rather than listed, because a chip is a chip and not a form.
 *
 * @param agency - The active agency.
 * @param t - Translation function.
 * @returns The chip, or null when the profile is complete.
 */
function buildProfilePending(agency: AgencyValuesType, t: TFunction): SettingsPendingItem | null {
  const empty = findEmptyProfileFields(agency);
  if (empty.length === 0) return null;
  return {
    key: 'perfil-dados',
    section: 'perfil',
    label: t('v2.pending.profile', { fields: joinFieldNames(empty.map((f) => t(f.labelKey)), t) }),
  };
}

/**
 * How many field names a chip will spell out before it starts counting.
 *
 * Three is where the chip stops being a label and starts being a sentence;
 * past that the count carries more than the list does.
 */
const MAX_NAMED_FIELDS = 3;

/**
 * Joins field names the way the sentence wants them read.
 *
 * "2 dados em falta" told the reader there was a problem and nothing about
 * what it was, which left them opening the tab to find out — the exact trip
 * the band exists to save. Naming the fields makes the chip worth reading
 * on its own.
 *
 * @param names - The already-localized field names, in display order.
 * @param t - Translation function.
 * @returns The joined list, with an overflow count past {@link MAX_NAMED_FIELDS}.
 */
function joinFieldNames(names: string[], t: TFunction): string {
  if (names.length > MAX_NAMED_FIELDS) {
    const shown = names.slice(0, MAX_NAMED_FIELDS).join(', ');
    return t('v2.pending.andMore', { fields: shown, count: names.length - MAX_NAMED_FIELDS });
  }
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} ${t('v2.pending.and')} ${names[names.length - 1]}`;
}

/**
 * Builds the portal chip for a portal that is connected but not sending.
 *
 * A portal that was never connected is not a pendency — it is a choice, and
 * nagging about it would make the band permanent furniture. A portal that is
 * connected with its sending switched off is a half-finished setup: the
 * account exists, the listings are simply not going out.
 *
 * @param portals - The connected/enabled portal ids.
 * @param portalLabels - Localized portal names by id.
 * @param t - Translation function.
 * @returns The chip, or null when nothing is paused.
 */
function buildPortalPending(
  portals: PortalPendingInput | null,
  portalLabels: Record<string, string>,
  t: TFunction,
): SettingsPendingItem | null {
  if (!portals) return null;
  const paused = portals.connected_portals.filter((id) => !portals.enabled_portals.includes(id));
  if (paused.length === 0) return null;
  const label = paused.length === 1
    ? t('v2.pending.portalPausedOne', { portal: portalLabels[paused[0]] ?? paused[0] })
    : t('v2.pending.portalPausedMany', { count: paused.length });
  return { key: 'portais-pausados', section: 'portais', label };
}

/**
 * Builds the pending band's chips from the data the surface already has.
 *
 * Filtered by the same {@link isSettingsSectionVisible} that filters the
 * tabs: a chip pointing at a tab the member cannot open is worse than no
 * chip, because it advertises a fix they have no way to apply.
 *
 * @param input - The agency, portal state, labels, translator and nav gates.
 * @returns The chips to render, in tab order.
 */
export function buildSettingsPending(input: BuildPendingInput): SettingsPendingItem[] {
  const { agency, portals, portalLabels, t, navOptions } = input;
  const candidates = [
    buildProfilePending(agency, t),
    buildPortalPending(portals, portalLabels, t),
  ];
  return candidates.filter((item): item is SettingsPendingItem =>
    item !== null && isSettingsSectionVisible(item.section, navOptions));
}
